import { ComponentChildren } from 'preact'

import type { SharedProps } from '@bonniernews/dn-design-system-web/assets/types/shared-props.ts'

import { formatClassString } from '@bonniernews/dn-design-system-web/helpers/formatClassString.ts'

import type { DsIcon } from '@bonniernews/dn-design-system-web/types-lib/ds-icon.d.ts'

import { IconUse } from '@bonniernews/dn-design-system-web/components/icon-sprite/icon-sprite.tsx'

export interface DisclaimerProps extends SharedProps {
  /** Intended use is text, links and inlined icons */
  bodyHtml?: string
  /** For all available icons see: https://designsystem.dn.se/?path=/story/foundations-icons--all-icons */
  iconName?: DsIcon
  /** The variant to render */
  variant?: 'default' | 'native'
  children?: ComponentChildren
}

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/components/disclaimer](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/disclaimer)
 * - Storybook: [Disclaimer](https://designsystem.dn.se/?path=/docs/basic-disclaimer--docs)
 *
 * The component will not include styling by itself. Make sure to include the right styles for the component. See example below:
 * `@use '@bonniernews/dn-design-system-web/components/disclaimer/disclaimer.scss'`
 */
export const Disclaimer = ({
  bodyHtml,
  iconName,
  variant = 'default',
  classNames,
  attributes,
  children,
}: DisclaimerProps) => {
  const componentClassName = 'ds-disclaimer'
  const classNamePrefix = `${componentClassName}--`

  const classes = formatClassString([
    componentClassName,
    `${classNamePrefix}${variant}`,
    iconName && `${classNamePrefix}with-icon`,
    classNames,
  ])

  return (
    <section className={classes} {...attributes}>
      {iconName && (
        <span className={`${componentClassName}__icon-wrapper`}>
          <IconUse iconName={iconName} />
        </span>
      )}
      <div className={`${componentClassName}__body-html`} dangerouslySetInnerHTML={{ __html: bodyHtml || '' }}>
        {children}
      </div>
    </section>
  )
}
